import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Section, DataCard, Empty } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Navigation, Camera, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import ExportImport from "@/components/ExportImport";
import { useAuth } from "@/lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Fix default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export default function Absensi() {
  const { user: me } = useAuth();
  const canSupervise = me?.role === "admin" || me?.role === "operasional";
  const [records, setRecords] = useState([]);
  const [live, setLive] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [users, setUsers] = useState([]);
  const [superviseUser, setSuperviseUser] = useState("");

  const [showCam, setShowCam] = useState(false);
  const [pendingType, setPendingType] = useState(null);
  const [photo, setPhoto] = useState(null); // dataURL
  const [pos, setPos] = useState(null);
  const [viewPhoto, setViewPhoto] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  const load = async () => {
    const [a, l, u] = await Promise.all([
      api.get("/absensi"),
      api.get("/absensi/live").catch(() => ({ data: [] })),
      api.get("/users").catch(() => ({ data: [] })),
    ]);
    setRecords(a.data || []);
    setLive(l.data || []);
    setUsers(u.data || []);
  };
  useEffect(() => { load(); }, []);

  const superviseAbsen = (type) => {
    if (!superviseUser) return toast.error("Pilih karyawan dulu");
    if (!navigator.geolocation) return toast.error("Browser tidak dukung GPS");
    setLoading(true);
    navigator.geolocation.getCurrentPosition(async (p) => {
      try {
        await api.post("/absensi/for-user", {
          user_id: superviseUser, lat: p.coords.latitude, lng: p.coords.longitude, type,
        });
        toast.success(`Absen ${type} tercatat untuk karyawan`);
        setSuperviseUser("");
        load();
      } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
      finally { setLoading(false); }
    }, (err) => { toast.error("Gagal ambil lokasi: " + err.message); setLoading(false); },
    { enableHighAccuracy: true, timeout: 10000 });
  };

  // sync offline queue
  useEffect(() => {
    const syncQueue = async () => {
      const queue = JSON.parse(localStorage.getItem("absensi_queue") || "[]");
      if (!queue.length || !navigator.onLine) return;
      const remaining = [];
      let synced = 0;
      for (const item of queue) {
        try {
          const { queued_at, ...payload } = item;
          await api.post("/absensi", payload);
          synced++;
        } catch { remaining.push(item); }
      }
      if (remaining.length === 0) localStorage.removeItem("absensi_queue");
      else localStorage.setItem("absensi_queue", JSON.stringify(remaining));
      if (synced > 0) {
        toast.success(`${synced} absensi offline tersinkron${remaining.length ? `, ${remaining.length} masih tertunda` : ""}`);
        load();
      }
    };
    syncQueue();
    const onOnline = () => syncQueue();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  const startCheck = (type) => {
    if (!navigator.geolocation) return toast.error("Browser tidak mendukung GPS");
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        setPos({ lat: p.coords.latitude, lng: p.coords.longitude });
        setPendingType(type);
        setPhoto(null);
        setShowCam(true);
        setLoading(false);
        // start camera
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play();
          }
        } catch (err) {
          toast.warning("Kamera tidak tersedia, submit tanpa foto");
        }
      },
      (err) => {
        toast.error("Gagal ambil lokasi: " + err.message);
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = canvasRef.current || document.createElement("canvas");
    canvas.width = 480; canvas.height = 360;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.6);
    setPhoto(dataUrl);
  };

  const closeCam = () => {
    setShowCam(false);
    setPhoto(null);
    setPendingType(null);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const submitCheck = async () => {
    const payload = { lat: pos.lat, lng: pos.lng, type: pendingType, photo_base64: photo || null };
    try {
      const { data } = await api.post("/absensi", payload);
      toast.success(pendingType === "check_in" ? "Check-in tercatat" : "Check-out tercatat");
      setStatus({ ...data, ts: new Date().toISOString() });
      closeCam();
      load();
    } catch (err) {
      // Include photo_base64 in offline queue so selfie also syncs
      const queue = JSON.parse(localStorage.getItem("absensi_queue") || "[]");
      queue.push({ ...payload, queued_at: new Date().toISOString() });
      localStorage.setItem("absensi_queue", JSON.stringify(queue));
      toast.warning("Offline — antrian (termasuk foto) tersimpan untuk sinkron nanti");
      closeCam();
    }
  };

  // Map center: first live location or Indonesia default
  const mapCenter = live[0] ? [live[0].lat, live[0].lng] : [-2.5, 118];
  const mapZoom = live[0] ? 12 : 5;

  return (
    <div>
      <PageHeader
        title="Absensi & GPS"
        subtitle="Check-in/out otomatis + selfie + peta lokasi karyawan"
        testId="absensi-header"
        actions={<ExportImport module="absensi" canImport={false} />}
      />
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
          <div className="lg:col-span-2 border border-neutral-800 bg-[#141414] p-6">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF6600] mb-1">// LIVE GEOLOCATION + SELFIE</div>
            <h3 className="font-display font-black text-2xl mb-4">Absen Sekarang</h3>
            <div className="grid grid-cols-2 gap-3">
              <Button
                data-testid="check-in-btn" onClick={() => startCheck("check_in")} disabled={loading}
                className="h-16 rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white font-display font-black text-lg"
              >
                <Navigation size={18} className="mr-2" /> CHECK-IN
              </Button>
              <Button
                data-testid="check-out-btn" onClick={() => startCheck("check_out")} disabled={loading}
                className="h-16 rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-display font-black text-lg"
              >
                <MapPin size={18} className="mr-2" /> CHECK-OUT
              </Button>
            </div>
            {status && (
              <div className="mt-4 p-3 border border-neutral-800 font-mono text-xs text-neutral-400">
                Terakhir: {status.type} @ {status.lat.toFixed(5)}, {status.lng.toFixed(5)}
                {status.photo_url && " (dengan foto ✓)"}
              </div>
            )}
          </div>
          <DataCard label="Total Absensi" value={records.length} accent="orange" testId="absensi-total" />
        </div>

        {canSupervise && (
          <div className="border border-neutral-800 bg-[#141414] p-5 mb-6" data-testid="supervise-panel">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700] mb-1">// MODE SUPERVISOR — ABSENKAN KARYAWAN LAIN</div>
            <h3 className="font-display font-black text-xl mb-4">Absen untuk Karyawan di Site</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Select value={superviseUser} onValueChange={setSuperviseUser}>
                <SelectTrigger data-testid="supervise-user-select" className="rounded-none bg-neutral-900 border-neutral-800 h-12">
                  <SelectValue placeholder="Pilih karyawan..." />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                  {users.filter((u) => u.id !== me?.id).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => superviseAbsen("check_in")} disabled={!superviseUser || loading} data-testid="supervise-checkin" className="h-12 rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white font-bold">
                <Navigation size={14} className="mr-1" /> Check-in Karyawan
              </Button>
              <Button onClick={() => superviseAbsen("check_out")} disabled={!superviseUser || loading} data-testid="supervise-checkout" className="h-12 rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold">
                <MapPin size={14} className="mr-1" /> Check-out Karyawan
              </Button>
            </div>
            <div className="text-xs text-neutral-500 mt-3">Menggunakan GPS lokasi Anda saat ini. Karyawan harus berada di site yang sama dengan Anda.</div>
          </div>
        )}

        {/* MAP */}
        <div className="mb-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">// PETA LIVE KARYAWAN</div>
          <div className="border border-neutral-800 bg-[#141414]" style={{ height: 420 }} data-testid="live-map">
            <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: "100%", width: "100%", background: "#0A0A0A" }}>
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; OSM &copy; Carto'
              />
              {live.map((l) => (
                <React.Fragment key={l.user_id}>
                  <CircleMarker
                    center={[l.lat, l.lng]} radius={12}
                    pathOptions={{ color: l.type === "check_in" ? "#16A34A" : "#FF6600", fillOpacity: 0.4 }}
                  />
                  <Marker position={[l.lat, l.lng]}>
                    <Popup>
                      <div className="font-mono text-xs">
                        <div className="font-bold">{l.user_name}</div>
                        <div className={l.type === "check_in" ? "text-green-600" : "text-orange-600"}>{l.type}</div>
                        <div>{new Date(l.timestamp).toLocaleString("id-ID")}</div>
                      </div>
                    </Popup>
                  </Marker>
                </React.Fragment>
              ))}
            </MapContainer>
          </div>
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">// RIWAYAT ABSENSI</div>
          {records.length === 0 ? <Empty /> : (
            <div className="border border-neutral-800 bg-[#141414]">
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800 hover:bg-transparent">
                    <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Waktu</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Karyawan</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Tipe</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Lokasi</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Foto</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.slice(0, 50).map((r) => (
                    <TableRow key={r.id} className="border-neutral-800">
                      <TableCell className="font-mono text-xs">{new Date(r.timestamp).toLocaleString('id-ID')}</TableCell>
                      <TableCell className="font-mono">{r.user_name}</TableCell>
                      <TableCell className="font-mono text-xs">{r.type}</TableCell>
                      <TableCell className="font-mono text-xs text-neutral-400">{r.lat?.toFixed(4)}, {r.lng?.toFixed(4)}</TableCell>
                      <TableCell>
                        {r.photo_url ? (
                          <button onClick={() => setViewPhoto(r.photo_url)} className="text-[#FF6600] hover:text-white" data-testid={`view-photo-${r.id}`}>
                            <ImageIcon size={16} />
                          </button>
                        ) : <span className="text-neutral-600 text-xs">-</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </Section>

      {/* Camera Dialog */}
      <Dialog open={showCam} onOpenChange={(o) => !o && closeCam()}>
        <DialogContent className="bg-[#0A0A0A] border-neutral-800 rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl">Ambil Selfie untuk {pendingType === "check_in" ? "Check-in" : "Check-out"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!photo ? (
              <>
                <div className="bg-black border border-neutral-800 overflow-hidden aspect-[4/3]">
                  <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" variant="outline" onClick={closeCam} className="rounded-none border-neutral-800">Batal</Button>
                  <Button type="button" onClick={capturePhoto} data-testid="capture-photo-btn" className="rounded-none bg-[#FFD700] hover:bg-[#e6c200] text-black font-bold">
                    <Camera size={16} className="mr-1" /> AMBIL FOTO
                  </Button>
                </div>
                <div className="text-xs text-neutral-500 text-center">Atau lewati foto (tidak disarankan)</div>
                <Button type="button" onClick={submitCheck} variant="outline" className="w-full rounded-none border-neutral-800">Lewati & Submit</Button>
              </>
            ) : (
              <>
                <div className="border border-neutral-800">
                  <img src={photo} alt="preview" className="w-full" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" variant="outline" onClick={() => setPhoto(null)} className="rounded-none border-neutral-800">Ulangi</Button>
                  <Button type="button" onClick={submitCheck} data-testid="submit-absensi-btn" className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white font-bold">
                    KIRIM ABSENSI →
                  </Button>
                </div>
              </>
            )}
            <canvas ref={canvasRef} className="hidden" />
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo viewer */}
      <Dialog open={!!viewPhoto} onOpenChange={(o) => !o && setViewPhoto(null)}>
        <DialogContent className="bg-[#0A0A0A] border-neutral-800 rounded-none max-w-xl">
          <DialogHeader><DialogTitle className="font-display font-black">Foto Absensi</DialogTitle></DialogHeader>
          {viewPhoto && (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${viewPhoto}?auth=${localStorage.getItem("erp_token") || ""}`}
              alt="absensi"
              className="w-full border border-neutral-800"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
